import { DraftChip } from 'components/drafts/DraftChip';
import { MinusIcon, type SvgIconComponent, XIcon } from 'components/icons';
import { ModalCountContext } from 'components/modals/ModalProvider';
import { Button } from 'components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogTitle } from 'components/ui/dialog';
import { useIsMobile } from 'hooks/useIsMobile';
import { useReducedMotion } from 'hooks/useReducedMotion';
import { useElevation } from 'hooks/useUiTheme';
import { cancelFrame, frame, m } from 'motion/react';
import { ComponentProps, ReactNode, RefObject, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { cn } from 'utils/cn';
import { useCreateDialogRequest } from './CreateDialogContext';
import { CreateDialogFooter } from './CreateDialogFooter';
import { createRecord } from './recordTypes';
import { DialogDraft, useDialogDraftKey } from './useCreateDraft';

type OpenChangeHandler = NonNullable<ComponentProps<typeof Dialog>['onOpenChange']>;

/** The surface the dialog and its tab share: minimised, it morphs from the dialog's box into the tab's, and back. */
export const CREATE_DIALOG_FRAME = 'create-dialog-frame';

/** Tailwind's `ease-out`, which the fades beside the frame run on. */
const EASE_OUT = [0, 0, 0.2, 1] as const;

/**
 * The morph's clock: minimise in 200ms, a step the `--duration-*` scale does not have, restore in `--duration-medium`.
 * Under reduced motion the dialog and the tab only cross-fade, in `--duration-quick`.
 */
export const MORPH = {
    minimise: { duration: 0.2, ease: EASE_OUT },
    restore: { duration: 0.35, ease: EASE_OUT },
    still: { duration: 0.15 },
} as const;

/** A corner of the frame as drawn, in px: Motion writes a moving frame's as a percentage of the box it scales. */
function cornerOf(radius: string, width: number) {
    return radius.endsWith('%') ? (parseFloat(radius) / 100) * width : parseFloat(radius) || 0;
}

/**
 * Holds `content` inside the frame while the frame travels between the dialog and the tab: anchored at the frame's
 * top-start corner and clipped by it, at its own size, so none of it shows outside the frame and no text scales.
 * From each change of `minimised` until the frame is at rest; content that rests where the frame does is let go.
 */
export function useFollowFrame(contentRef: RefObject<HTMLElement | null>, minimised: boolean, follows: boolean) {
    useLayoutEffect(() => {
        const content = contentRef.current;
        if (!follows || !content) {
            return undefined;
        }
        let x = 0;
        let y = 0;
        let moved = false;
        const until = performance.now() + 1000;
        const release = () => {
            content.style.translate = '';
            content.style.clipPath = '';
        };
        const follow = () => {
            const box = content.getBoundingClientRect();
            const frameNode = document.querySelector<HTMLElement>(`[data-slot="${CREATE_DIALOG_FRAME}"]`);
            // A popup not shown yet, or hidden again, has no box.
            if (!frameNode || !box.width) {
                if (moved || performance.now() > until) {
                    cancelFrame(follow);
                }
                return;
            }
            const to = frameNode.getBoundingClientRect();
            x += to.left - box.left;
            y += to.top - box.top;
            const right = Math.max(0, box.width - to.width);
            const bottom = Math.max(0, box.height - to.height);
            const { borderTopLeftRadius, borderTopRightRadius, borderBottomRightRadius, borderBottomLeftRadius } =
                frameNode.style;
            const corners = [borderTopLeftRadius, borderTopRightRadius, borderBottomRightRadius, borderBottomLeftRadius]
                .map((radius) => `${cornerOf(radius, to.width)}px`)
                .join(' ');
            content.style.translate = `${x}px ${y}px`;
            content.style.clipPath = `inset(0 ${right}px ${bottom}px 0 round ${corners})`;
            // Motion takes its transform off the frame as the frame comes to rest.
            const still = !frameNode.style.transform || frameNode.style.transform === 'none';
            moved ||= !still;
            if ((still && moved) || performance.now() > until) {
                cancelFrame(follow);
                if (Math.max(Math.abs(x), Math.abs(y), right, bottom) < 0.5) {
                    release();
                }
            }
        };
        frame.postRender(follow, true);
        return () => {
            cancelFrame(follow);
            release();
        };
    }, [contentRef, minimised, follows]);
}

/** `rounded-xl`, the dialog's corners; the sheet has none. */
const DIALOG_RADIUS = 14;

/**
 * The top edge is fixed and the height follows the content, so opening a section never moves the header. Under
 * 768px it is a full-screen sheet. It does not clip: a dropdown that portals into it may hang past its edge.
 */
const POPUP_CLASS = cn(
    'top-[max(4dvh,16px)] flex max-h-[calc(100dvh-2*max(4dvh,16px))] translate-y-0 flex-col gap-0 p-0 sm:max-w-[720px]',
    'max-sm:top-0 max-sm:h-full max-sm:max-h-none max-sm:max-w-none max-sm:rounded-none',
    // The frame behind the content is the surface, and the content carries the ring. Minimised, the popup fades
    // without the kit's zoom: text never scales. Under reduced motion the popup and its backdrop fade by a transition,
    // as the kit's own animations are off there.
    'new:shadow-none data-closed:zoom-out-100 bg-transparent ring-0',
    'motion-reduce:transition-opacity motion-reduce:data-ending-style:opacity-0'
);

/** Holds the content over the frame, with the dialog's ring. Restored, it fades in as the frame grows back. */
const CONTENT_CLASS = 'ring-foreground/10 relative flex min-h-0 flex-1 flex-col rounded-xl ring-1 max-sm:rounded-none';

/** The popup's and its backdrop's timing as the dialog minimises, and as it is restored. */
const MINIMISE_CLASS = 'duration-200 ease-out motion-reduce:duration-(--duration-quick)';
const RESTORE_CLASS =
    'duration-(--duration-medium) ease-out motion-reduce:duration-(--duration-quick) motion-reduce:data-starting-style:opacity-0';

/**
 * The scrolling body. On the sheet every control in it is 44px high, whichever section it sits in. A text area of
 * one line is as high as the controls beside it: the kit's padding makes it 2px higher, and lower on the sheet.
 */
export const CREATE_DIALOG_BODY_CLASS = cn(
    'min-h-0 flex-1 overflow-y-auto overscroll-contain',
    'max-sm:[&_[data-slot=input-group]]:min-h-11 max-sm:[&_[data-slot=input]]:min-h-11',
    'max-sm:[&_[data-slot=combobox-chips]]:min-h-11 max-sm:[&_[data-slot=select-trigger]]:min-h-11',
    '[&_[data-slot=textarea]]:py-[7px] max-sm:[&_[data-slot=textarea]]:min-h-11 max-sm:[&_[data-slot=textarea]]:py-[9px]'
);

/** A pill beside the title; on the sheet a second line under it. */
const CHIP_CLASS = cn(
    'bg-neutral-tint text-foreground-secondary text-caption flex h-6 items-center rounded-full px-2.5 font-medium whitespace-nowrap',
    'max-sm:text-muted-foreground max-sm:block max-sm:h-auto max-sm:max-w-full max-sm:truncate max-sm:bg-transparent max-sm:px-0 max-sm:font-normal'
);

const HEADER_BUTTON_CLASS = 'text-foreground-secondary max-sm:size-11';

type HeaderProps = {
    icon: SvgIconComponent;
    tileClass: string;
    title: ReactNode;
    /** Before the chip: a draft's mark. */
    mark?: ReactNode;
    /** Where the creation started, or which record an edit is of. */
    chip?: ReactNode;
    /** After the title, where a form's own header pieces land. */
    suffix?: ReactNode;
    /** At the end, before Close. */
    children?: ReactNode;
};

/** The dialog's header: the record's tile, its title and chip, and Close. */
export function CreateDialogHeader({ icon: Icon, tileClass, title, mark, chip, suffix, children }: HeaderProps) {
    return (
        <div className="border-neutral-tint flex h-[60px] shrink-0 items-center gap-2.5 border-b ps-6 pe-3 max-sm:h-14 max-sm:ps-4 max-sm:pe-1.5">
            <span
                aria-hidden
                className={cn('flex size-7 shrink-0 items-center justify-center rounded-[7px]', tileClass)}
            >
                <Icon className="size-4" />
            </span>
            <div className="flex min-w-0 items-center gap-2.5 max-sm:flex-col max-sm:items-start max-sm:gap-0">
                <DialogTitle className="text-dialog-title truncate font-semibold whitespace-nowrap">
                    {title}
                </DialogTitle>
                {mark}
                {chip ? <span className={CHIP_CLASS}>{chip}</span> : null}
            </div>
            {suffix}
            <span className="flex-1" />
            {children}
            <DialogClose render={<Button variant="ghost" size="icon" className={HEADER_BUTTON_CLASS} />}>
                <XIcon className="size-[18px]" />
                <span className="sr-only">
                    <FormattedMessage id="close" />
                </span>
            </DialogClose>
        </div>
    );
}

/**
 * The create dialog around a form: the record's title in the header, the form in the scrolling body, and the
 * form's own actions in the footer. A draft being edited is titled by its name, with the Draft chip. Close,
 * Cancel and Escape close it, which asks first what becomes of anything typed. Where it can be shown as a tab, a
 * click outside and the header's Minimise button minimise it: the dialog is then closed and stays mounted,
 * hidden, so the form keeps everything, and the page is free of its overlay, its focus trap and its scroll lock.
 *
 * Minimise and restore morph the dialog's frame into its tab and back, the content of each fading inside it as it
 * goes (`useFollowFrame`). With motion allowed the frame is mounted only while the dialog is open, never on the
 * kept-mounted popup, so one element at a time carries the shared id; under reduced motion it carries none, stays,
 * and fades with the popup. A creation's first opening keeps the kit's own entrance.
 */
export function CreateDialogFrame({ children }: { children: ReactNode }) {
    const request = useCreateDialogRequest();
    const openPopups = useContext(ModalCountContext);
    const bodyRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    // What reopening puts back: how far the body was scrolled, and the field in it that last had the focus.
    const scrollTopRef = useRef(0);
    const focusedRef = useRef<HTMLElement | null>(null);
    const minimised = request?.minimised ?? false;
    const draft = useDialogDraftKey();
    const reduced = useReducedMotion();
    const isSheet = useIsMobile();
    const shadow = useElevation('dialog');
    // Every opening after the first minimise is a restore.
    const [restores, setRestores] = useState(false);
    if (minimised && !restores) {
        setRestores(true);
    }
    const timingClass = minimised ? MINIMISE_CLASS : restores ? RESTORE_CLASS : undefined;
    useFollowFrame(contentRef, minimised, restores && !reduced);

    // A hidden element does not keep its scroll position in every browser. A frame later, once it is drawn again.
    useEffect(() => {
        if (minimised || scrollTopRef.current === 0) {
            return undefined;
        }
        const frame = requestAnimationFrame(() => {
            if (bodyRef.current) {
                bodyRef.current.scrollTop = scrollTopRef.current;
            }
        });
        return () => cancelAnimationFrame(frame);
    }, [minimised]);

    if (!request) {
        return null;
    }
    const { record, chip, close, noteUserAction, setHeaderSlot, setFooterSlot, setErrorSlot } = request;
    const { titleId, icon: Icon, tileClass } = createRecord(record);

    const minimise = () => {
        scrollTopRef.current = bodyRef.current?.scrollTop ?? 0;
        request.minimise?.();
    };

    const handleOpenChange: OpenChangeHandler = (open, details) => {
        if (open) {
            return;
        }
        // Only a click on this dialog's own overlay is one: a dropdown and a popup are not outside it.
        if (details.reason === 'outside-press') {
            minimise();
            return;
        }
        // A popup opened from the form is a dialog of its own rather than a child of this one, so the Escape
        // that closes it reaches this dialog too.
        if (details.reason === 'escape-key' && (openPopups > 0 || details.event.defaultPrevented)) {
            details.cancel();
            return;
        }
        close();
    };

    return (
        <Dialog open={!minimised} onOpenChange={handleOpenChange} disablePointerDismissal={!request.minimise}>
            {/* Kept mounted, so a minimised dialog's form keeps everything. `data-form-body` is where a form's
                comboboxes portal their list. Here, not on the scrolling body, which would clip the list. The
                capture handlers hear the user before a field does. */}
            <DialogContent
                keepMounted
                showCloseButton={false}
                data-form-body
                className={cn(
                    POPUP_CLASS,
                    timingClass,
                    // Over the tab's frame, which arrives a step above the overlay: the fading form stays on top of the
                    // frame travelling out from under it. Only the opacity transitions, or the z-index would take the
                    // 200ms to climb past the frame's.
                    minimised && 'z-[calc(var(--z-modal)+2)] transition-opacity',
                    restores && 'data-open:animate-none'
                )}
                overlayProps={{
                    className: cn(
                        'motion-reduce:transition-opacity motion-reduce:data-ending-style:opacity-0',
                        timingClass
                    ),
                }}
                initialFocus={focusedRef}
                onPointerDownCapture={noteUserAction}
                onKeyDownCapture={noteUserAction}
                onChangeCapture={noteUserAction}
                onDropCapture={noteUserAction}
            >
                {/* Under reduced motion nothing morphs: the frame stays and fades with the popup; the key mounts it
                    afresh as that preference changes. It lays out anew only as the dialog minimises or restores, so a
                    form growing under it resizes it at once; Motion hands over only between ends whose dependency
                    differs. */}
                {reduced || !minimised ? (
                    <m.div
                        key={reduced ? 'still' : 'morph'}
                        data-slot={CREATE_DIALOG_FRAME}
                        layoutId={reduced ? undefined : CREATE_DIALOG_FRAME}
                        layoutDependency={minimised}
                        transition={MORPH.restore}
                        className="bg-popover absolute inset-0"
                        style={{ borderRadius: isSheet ? 0 : DIALOG_RADIUS, boxShadow: shadow }}
                    />
                ) : null}
                <div
                    ref={contentRef}
                    // The fade's own duration: a `duration-*` would also make the frame's clip and offset transition.
                    className={cn(
                        CONTENT_CLASS,
                        restores &&
                            !minimised &&
                            'animate-in fade-in-0 animation-duration-(--duration-medium) ease-out motion-reduce:animate-none'
                    )}
                >
                    <CreateDialogHeader
                        icon={Icon}
                        tileClass={tileClass}
                        title={
                            draft ? (
                                <DialogDraft draft={draft}>
                                    {(detail) => detail?.name ?? <FormattedMessage id={titleId} />}
                                </DialogDraft>
                            ) : (
                                <FormattedMessage id={titleId} />
                            )
                        }
                        mark={draft ? <DraftChip className="shrink-0" /> : null}
                        chip={chip}
                        suffix={<div ref={setHeaderSlot} className="flex min-w-0 items-center gap-2" />}
                    >
                        {request.minimise ? (
                            <Button variant="ghost" size="icon" className={HEADER_BUTTON_CLASS} onClick={minimise}>
                                {/* The glyph is a centred bar; minimise is drawn as one on the baseline. */}
                                <MinusIcon className="size-[18px] translate-y-1" />
                                <span className="sr-only">
                                    <FormattedMessage id="create-dialog.actions.minimise" />
                                </span>
                            </Button>
                        ) : null}
                    </CreateDialogHeader>
                    <div
                        ref={bodyRef}
                        className={CREATE_DIALOG_BODY_CLASS}
                        onFocus={(event) => {
                            // Focus events of a list drawn outside the body reach here too, through React's tree.
                            if (event.currentTarget.contains(event.target)) {
                                focusedRef.current = event.target;
                            }
                        }}
                    >
                        {children}
                    </div>
                    <CreateDialogFooter slotRef={setFooterSlot} errorSlotRef={setErrorSlot} />
                </div>
            </DialogContent>
        </Dialog>
    );
}
